import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { campaignApi, type NearbyTreeApi } from "@/lib/api";
import type { FieldGpsState } from "../useFieldGps";

interface FieldMapProps {
  gps: FieldGpsState;
  projectId?: number;
  onClose: () => void;
}

const RADIUS_OPTIONS = [50, 100, 250, 500];

function formatDistance(m: number): string {
  return m < 1000 ? `${m.toFixed(m < 10 ? 1 : 0).replace(".", ",")} m` : `${(m / 1000).toFixed(1).replace(".", ",")} km`;
}

/** Exemplares cadastrados perto da posicao real do aparelho (GET /trees/nearby). */
export default function FieldMap({ gps, projectId, onClose }: FieldMapProps) {
  const [radius, setRadius] = useState(100);
  const [items, setItems] = useState<NearbyTreeApi[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);

  // Arredonda a posicao para nao refazer a consulta a cada pequena oscilacao do GPS.
  const lat = gps.latitude !== null ? Number(gps.latitude.toFixed(5)) : null;
  const lng = gps.longitude !== null ? Number(gps.longitude.toFixed(5)) : null;

  useEffect(() => {
    if (lat === null || lng === null) return;
    const current = ++seq.current;
    setLoading(true);
    setError(null);
    campaignApi
      .nearbyTrees({ latitude: lat, longitude: lng, radius_m: radius, project_id: projectId })
      .then((rows) => {
        if (current === seq.current) setItems(rows);
      })
      .catch((err: unknown) => {
        if (current === seq.current) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (current === seq.current) setLoading(false);
      });
  }, [lat, lng, radius, projectId]);

  return (
    <div className="px-4 pt-4 pb-20 max-w-lg md:max-w-2xl mx-auto">
      <div className="flex items-center gap-2 mb-4">
        <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-[#E8EAED] flex items-center justify-center" aria-label="Voltar">
          <i className="ri-arrow-left-line text-[#3F4A52] text-lg" />
        </button>
        <h2 className="text-lg font-heading font-bold text-[#3F4A52]">Exemplares próximos</h2>
      </div>

      <div className="flex gap-2 mb-4 overflow-x-auto no-scrollbar pb-1">
        {RADIUS_OPTIONS.map((r) => (
          <button
            key={r}
            onClick={() => setRadius(r)}
            className={`px-4 py-2 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
              radius === r ? "bg-[#0052CC] text-white" : "bg-white border border-[#E8EAED] text-[#3F4A52]"
            }`}
          >
            {r} m
          </button>
        ))}
      </div>

      {lat === null || lng === null ? (
        <div className="bg-white border border-dashed border-[#E8EAED] rounded-xl px-4 py-6 text-center">
          <p className="text-sm text-[#3F4A52]">
            {gps.status === "indisponivel" ? gps.error || "Localização indisponível." : "Aguardando a posição do aparelho..."}
          </p>
          <p className="text-xs text-[#3F4A52]/60 mt-1">A busca usa a posição real do GPS.</p>
        </div>
      ) : loading ? (
        <p className="text-xs text-[#3F4A52]/60">Buscando exemplares...</p>
      ) : error ? (
        <p className="text-xs text-[#D32F2F]">Não foi possível consultar: {error}</p>
      ) : items.length === 0 ? (
        <div className="bg-white border border-dashed border-[#E8EAED] rounded-xl px-4 py-6 text-center">
          <p className="text-sm text-[#3F4A52]">Nenhum exemplar cadastrado num raio de {radius} m.</p>
        </div>
      ) : (
        <div>
          <p className="text-xs text-[#3F4A52]/60 mb-2">
            {items.length} {items.length === 1 ? "exemplar" : "exemplares"} num raio de {radius} m
          </p>
          <div className="space-y-2">
            {items.map((a) => (
              <div key={a.id} className="p-3 rounded-xl border border-[#E8EAED] bg-white">
                <Link to={`/cta/${a.id}`} className="flex items-center gap-3 text-left">
                  <div className="w-10 h-10 rounded-lg bg-[#E8EAED] flex items-center justify-center flex-shrink-0">
                    <i className="ri-leaf-line text-[#1B6E4F] text-lg" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-[#3F4A52] truncate">
                      {a.species_popular_name || a.species_scientific_name || "Espécie não identificada"}
                    </p>
                    <p className="text-xs text-[#3F4A52]/50 font-mono">{a.cta_code}</p>
                  </div>
                  <span className="text-xs text-[#3F4A52]/70 whitespace-nowrap">{formatDistance(a.distance_m)}</span>
                </Link>
                <div className="grid grid-cols-3 gap-2 mt-3">
                  <Link to={`/cta/${a.id}`} className="py-2 rounded-lg border border-[#E8EAED] text-center text-xs font-medium text-[#3F4A52]">
                    Abrir ficha
                  </Link>
                  <Link to={`/coletor/exemplar/${a.id}`} className="py-2 rounded-lg border border-[#1B6E4F] text-center text-xs font-medium text-[#1B6E4F]">
                    Fotos
                  </Link>
                  <Link to={`/cta/${a.id}/inspecoes/nova`} className="py-2 rounded-lg bg-[#0052CC] text-center text-xs font-heading font-bold text-white">
                    Nova vistoria
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
